import { useContext, useEffect, useState } from "react";
import WorkIcon from "@mui/icons-material/Work";
import {
  useCargosApi,
  CargoItem,
  CargoDto,
} from "../../../services/api/cargosApi";
import CargosModal from "../../../components/module/admin/CargosModal/CargosModal";
import CargoDataTable from "../../../components/module/admin/GenericDataTable/CargoDataTable";
import PageLayout from "../../../components/layout/PageLayout";
import Swal from "sweetalert2";
import { toast } from "../../../utils/toast";
import { GridColDef, GridRenderCellParams } from "@mui/x-data-grid";
import {
  Box,
  IconButton,
  Switch,
  Tooltip,
} from "@mui/material";
import { Edit as EditIcon, Delete as DeleteIcon } from "@mui/icons-material";
import { DarkModeContext } from "../../../context/darkModeContext";

const Cargos = () => {
  const [items, setItems] = useState<CargoItem[]>([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingItem, setEditingItem] = useState<CargoItem | null>(null);
  const { isLoading, getAll, create, update, remove } = useCargosApi();
  const { darkMode } = useContext(DarkModeContext);
  
  const columns: GridColDef[] = [
    { field: "Id", headerName: "ID", width: 90 },
    { field: "Nombre", headerName: "Nombre del Cargo", flex: 1, minWidth: 200 },
    {
      field: "area",
      headerName: "Área",
      flex: 1,
      minWidth: 200,
      valueGetter: (params) => params.row.area?.Nombre || "",
    },
    {
      field: "Activo",
      headerName: "Estado",
      width: 120,
      renderCell: (params: GridRenderCellParams) => (
        <Switch
          checked={params.row.Activo}
          onChange={(e) => handleStatusChange(params.row.Id, e.target.checked)}
          color="primary"
        />
      ),
    },
    {
      field: "actions",
      headerName: "Acciones",
      width: 120,
      sortable: false,
      renderCell: (params: GridRenderCellParams) => (
        <Box>
          <Tooltip title="Editar">
            <IconButton onClick={() => handleEdit(params.row)} color="primary" size="small">
              <EditIcon />
            </IconButton>
          </Tooltip>
          <Tooltip title="Eliminar">
            <IconButton onClick={() => handleDelete(params.row.Id)} color="error" size="small">
              <DeleteIcon />
            </IconButton>
          </Tooltip>
        </Box>
      ),
    },
  ];

  const loadItems = async () => {
    const data = await getAll();
    if (Array.isArray(data)) {
      setItems(data);
    }
  };

  useEffect(() => {
    loadItems();
  }, []);

  const handleAdd = () => {
    setEditingItem(null);
    setIsModalOpen(true);
  };

  const handleEdit = (item: CargoItem) => {
    setEditingItem(item);
    setIsModalOpen(true);
  };

  const handleDelete = async (id: number) => {
    const result = await Swal.fire({
      title: "¿Está seguro?",
      text: "Esta acción eliminará el cargo de forma permanente.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Sí, eliminar",
      cancelButtonText: "Cancelar",
      confirmButtonColor: '#3498db',
      cancelButtonColor: '#e74c3c',
    });

    if (result.isConfirmed) {
      try {
        await remove(id);
        await loadItems();
        toast.success("El cargo ha sido eliminado correctamente.");
      } catch (error) {
        console.error("Error deleting item:", error);
        toast.error("No se pudo eliminar el registro.");
      }
    }
  };

  const handleStatusChange = async (id: number, active: boolean) => {
    const result = await Swal.fire({
      title: "¿Está seguro?",
      text: `¿Desea ${active ? "activar" : "desactivar"} este cargo?`,
      icon: "question",
      showCancelButton: true,
      confirmButtonText: `Sí, ${active ? "activar" : "desactivar"}`,
      cancelButtonText: "Cancelar",
      confirmButtonColor: '#3498db',
      cancelButtonColor: '#e74c3c',
    });

    if (result.isConfirmed) {
      try {
        const item = items.find((item) => item.Id === id);
        if (item) {
          const updateData: CargoDto = {
            IdArea: item.IdArea,
            Nombre: item.Nombre,
            Activo: active,
          };
          await update(id, updateData);
          await loadItems();
          toast.success("El estado del cargo ha sido actualizado.");
        }
      } catch (error) {
        console.error("Error updating status:", error);
        toast.error("No se pudo actualizar el estado.");
      }
    }
  };

  const handleModalSubmit = async (data: CargoDto) => {
    try {
      if (editingItem) {
        await update(editingItem.Id, data);
      } else {
        await create(data);
      }
      setIsModalOpen(false);
      await loadItems();
      toast.success("Registro guardado correctamente");
    } catch (error) {
      console.error("Error saving item:", error);
      toast.error("No se pudo guardar el registro.");
    }
  };

  return (
    <PageLayout icon={WorkIcon} title="Cargos">
        <div style={{ padding: "24px" }}>
          <CargoDataTable
            items={items}
            columns={columns}           
            onEdit={handleEdit}
            onDelete={handleDelete}
            onStatusChange={handleStatusChange}
            onAdd={handleAdd}
            isLoading={isLoading}
          />
          <CargosModal
            isOpen={isModalOpen}
            onClose={() => setIsModalOpen(false)}
            onSubmit={handleModalSubmit}
            title={editingItem ? "Editar Cargo" : "Nuevo Cargo"}
            initialData={
              editingItem
                ? {
                    IdArea: editingItem.IdArea,
                    Nombre: editingItem.Nombre,
                    Activo: editingItem.Activo,
                  }
                : undefined
            }
            isLoading={isLoading}
            isEdit={!!editingItem}
          />
        </div>
      </PageLayout>
  );
};

export default Cargos;